import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Tag } from "@/components/common";
import { Sparkle, Microphone, Spinner, Stop, Check, ArrowLeft, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CreateStudio() {
  const { refresh, switchStudio } = useStudio();
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [creating, setCreating] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const propose = async () => {
    if (!idea.trim()) { toast.error("Describe your studio idea"); return; }
    setBusy(true); setProposal(null);
    try {
      const { data } = await api.post("/studios/propose", { idea });
      setProposal(data.proposal);
      toast.success("Studio proposal ready — review before creating");
    } catch { toast.error("Could not generate proposal"); }
    finally { setBusy(false); }
  };

  const mic = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); setRecording(false);
        const fd = new FormData();
        fd.append("file", new Blob(chunksRef.current, { type: "audio/webm" }), "c.webm");
        try {
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setIdea((p) => (p ? p + " " : "") + data.text);
        } catch { toast.error("Transcription failed"); }
      };
      mr.start(); setRecording(true);
    } catch { toast.error("Microphone denied"); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/studios", { studio: proposal });
      await refresh();
      switchStudio(data.id);
      toast.success(`${data.name} created — now your active studio`);
      navigate("/");
    } catch { toast.error("Could not create studio"); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <button onClick={() => navigate("/studios")} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4"><ArrowLeft size={15} /> Back to studios</button>
      <PageHeader icon={Buildings} title="Create New Studio"
        subtitle="Describe a brand new animation brand by text or voice. Studio AI designs the full concept — you approve before anything permanent is created." />

      <Panel className="mb-6">
        <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Sparkle size={16} weight="fill" className="text-[#00E5FF]" /> Your studio idea</div>
        <textarea data-testid="studio-idea" value={idea} onChange={(e) => setIdea(e.target.value)} rows={3}
          placeholder="e.g. Create a cinematic anime studio for teenagers. The story is set in the future and the main character can see people's memories."
          className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 resize-none" />
        <div className="mt-3 flex items-center gap-3">
          <button data-testid="studio-propose" onClick={propose} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-magic px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy ? <Spinner size={18} className="animate-spin" /> : <Sparkle size={18} weight="fill" />} {busy ? "Designing studio…" : "Design Studio"}
          </button>
          <button onClick={mic} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 transition-colors ${recording ? "border-[#FF3366] text-[#FF3366] animate-pulseGlow" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
            {recording ? <Stop size={18} weight="fill" /> : <Microphone size={18} weight="duotone" />} {recording ? "Stop" : "Voice"}
          </button>
        </div>
      </Panel>

      {proposal && (
        <Panel data-testid="studio-proposal" className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: `${proposal.color || "#00E5FF"}22`, border: `1px solid ${proposal.color || "#00E5FF"}55` }}>{proposal.emoji || "✨"}</span>
            <div>
              <h3 className="font-display text-2xl font-black text-white">{proposal.name}</h3>
              <p className="text-sm text-zinc-400">{proposal.concept}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {[["Audience", proposal.audience], ["Age", proposal.age_range], ["Genre", proposal.genre], ["Style", proposal.style], ["Tone", proposal.tone], ["Main language", proposal.main_language]].map(([k, v]) => v && (
              <div key={k}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-sm text-white mt-0.5">{v}</div></div>
            ))}
          </div>
          {proposal.languages?.length > 0 && <div className="mb-4"><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Languages</div><div className="flex flex-wrap gap-2">{proposal.languages.map((l) => <Tag key={l}>{l}</Tag>)}</div></div>}
          {[["Story direction", proposal.story_direction], ["Music direction", proposal.music_direction], ["YouTube strategy", proposal.youtube_strategy], ["Visual identity", proposal.visual_identity]].map(([k, v]) => v && (
            <div key={k} className="mb-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-sm text-zinc-300 mt-0.5">{v}</div></div>
          ))}
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {proposal.characters?.length > 0 && (
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Initial characters</div>
                {proposal.characters.map((c, i) => <div key={i} className="rounded-lg border border-white/8 bg-black/20 px-3 py-2 mb-2"><span className="text-sm text-white font-medium">{c.name}</span><p className="text-xs text-zinc-500">{c.description}</p></div>)}
              </div>)}
            {proposal.worlds?.length > 0 && (
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Initial worlds</div>
                {proposal.worlds.map((w, i) => <div key={i} className="rounded-lg border border-white/8 bg-black/20 px-3 py-2 mb-2"><span className="text-sm text-white font-medium">{w.name}</span><p className="text-xs text-zinc-500">{w.description}</p></div>)}
              </div>)}
          </div>
          <div className="mt-5 flex gap-2">
            <button data-testid="studio-create" onClick={create} disabled={creating}
              className="inline-flex items-center gap-2 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D] px-5 py-2.5 text-sm font-semibold hover:bg-[#00FF9D]/25 transition-colors disabled:opacity-60">
              {creating ? <Spinner size={16} className="animate-spin" /> : <Check size={16} weight="bold" />} Create Studio
            </button>
            <button onClick={propose} className="rounded-full border border-white/10 bg-[#1C1C1C] px-5 py-2.5 text-sm text-white hover:border-white/30 transition-colors">Regenerate</button>
          </div>
        </Panel>
      )}
    </div>
  );
}
